import { TAX_STATUS_LABEL } from '@/lib/rebalanceLabels'
import type { ProposedAction } from '@/types/rebalance'

const BOM = '﻿'
const SEP = ';'
const EOL = '\r\n'

const HEADER = [
  'Nr',
  'Richtung',
  'Ticker',
  'Name',
  'Betrag_EUR',
  'Cluster',
  'Quelle',
  'Steuer_Status',
  'KESt_EUR',
]

/** Decimal-String → deutsches Dezimal-Komma, 2 Nachkommastellen, ohne Tausender. */
function toDeDecimal(value: string): string {
  const num = Number(value)
  if (!Number.isFinite(num)) return value
  return num.toFixed(2).replace('.', ',')
}

/** RFC-4180: Feld quoten, wenn es Separator, Quote oder Zeilenumbruch enthält. */
function escapeField(field: string): string {
  if (/[;"\n\r]/.test(field)) {
    return `"${field.replace(/"/g, '""')}"`
  }
  return field
}

function rowFor(action: ProposedAction, index: number): string {
  const richtung = action.action === 'sell' ? 'Verkauf' : 'Kauf'
  const quelle = action.source === 'position_drift' ? 'Position-Drift' : 'Cluster-Drift'
  const steuer =
    action.action === 'buy' || action.tax_status === 'n/a'
      ? ''
      : TAX_STATUS_LABEL[action.tax_status]
  const kest = action.kest_eur ? toDeDecimal(action.kest_eur) : ''

  const fields = [
    String(index),
    richtung,
    action.ticker,
    action.name,
    toDeDecimal(action.eur_amount),
    action.cluster_name ?? '',
    quelle,
    steuer,
    kest,
  ]
  return fields.map(escapeField).join(SEP)
}

/**
 * Baut den CSV-String der vorgeschlagenen Aktionen (deutsch-Excel-freundlich:
 * `;`-Separator, Dezimal-Komma, UTF-8-BOM, CRLF). Reihenfolge entspricht der
 * übergebenen `actions`-Reihenfolge.
 */
export function buildRebalanceCsv(actions: ProposedAction[]): string {
  const header = HEADER.join(SEP)
  const rows = actions.map((a, i) => rowFor(a, i + 1))
  return BOM + [header, ...rows].join(EOL)
}

/** YYYY-MM-DD aus lokalem Datum (nicht UTC, damit der Dateiname zur Anzeige passt). */
function dateStamp(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Lädt die vorgeschlagenen Aktionen als CSV herunter
 * (`rebalance-checkliste_YYYY-MM-DD.csv`). `today` injizierbar für Tests.
 */
export function downloadRebalanceCsv(actions: ProposedAction[], today: Date = new Date()): void {
  const csv = buildRebalanceCsv(actions)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `rebalance-checkliste_${dateStamp(today)}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
